import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce, usePathParam, useViewport } from '@chilecompra/react-kit'
import { Typography, Skeleton, DataTable } from '@chilecompra/react-kit/components'
import { ANNUAL_PURCHASE_PLAN_TABS } from '../../config/settings/constant'
import { PUBLIC_URL } from '../../config/settings/environment'
import { exportToExcel } from '../../modules/utils/exports'
import { calculateYAxisScale } from '../../modules/utils/helpers'
import { onGetAnnualPurchasePlanThunk } from './AnnualPurchasePlanSection.actions'
import ColumnChart from '../../components/Charts/ColumnChart'
import IconButton from '../../components/Buttons/IconButton'
import InfoLabel from '../../components/Labels/InfoLabel'
import {
  ArrowDownIcon,
  Card,
  CardItem,
  Column,
  Link,
  NotFoundImg,
  Root,
  Row,
  Separator,
  Strong,
  WarningImg
} from './AnnualPurchasePlanSection.styles'

/**
 * The Annual Purchase Plan section container.
 */
const AnnualPurchasePlanSection = () => {
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const {
    size: { isTiny }
  } = useViewport()
  const [showTable, setShowTable] = useState(false)

  const { error, loading, hasData, updateDate, publicationDate, categories, chartData, gridData, excelData } =
    useSelector(state => state.annualPurchasePlan)

  const lastUpdated = publicationDate ? ` el ${publicationDate}.` : '.'
  const isContentReady = !loading && !error
  const hasDataToShow = isContentReady && hasData
  const totalRows = 12
  const recordCount = hasData ? gridData.length : 0
  const hasPagination = recordCount > totalRows
  const tickScale = calculateYAxisScale(chartData)

  useEffectOnce(() => {
    dispatch(onGetAnnualPurchasePlanThunk({ dni }))
  }, [dispatch, dni])

  const handleShowTable = updatedValue => () => setShowTable(updatedValue)
  const handleDownload = () => exportToExcel(excelData, ANNUAL_PURCHASE_PLAN_TABS.DOWNLOAD_FILE_NAME)

  const handleKeyPressDownload = event => {
    if (event.charCode === 13 || event.charCode === 32) {
      event.preventDefault()
      handleDownload()
    }
  }

  return (
    <Root>
      {!loading && (
        <>
          <Row>
            <Column padding="10px 10px 0 0">
              <Typography tabIndex="0" color="black1" variant="h4" fontWeight="bold" fontSize="18px" lineHeight="22px">
                Ejecución del plan anual de compras
              </Typography>

              <Typography
                color="black3"
                display="inline"
                variant="body2"
                fontWeight="regular"
                fontSize="14px"
                lineHeight="18px"
                tabIndex="0"
              >
                <Strong>Fuente:</Strong> Plan anual de compras publicado por la institución en Mercado Público
                {hasData ? lastUpdated : '.'}
              </Typography>
            </Column>
          </Row>
          {hasDataToShow && (
            <Row>
              <Column maxWidth="595px" padding="10px 0 24px 0">
                <Typography
                  color="black1"
                  display="inline"
                  variant="body2"
                  fontWeight="regular"
                  fontSize="14px"
                  lineHeight="18px"
                  tabIndex="0"
                >
                  Revisa la ejecución de los montos planificados durante cada mes del año.{' '}
                  <Strong>
                    Esta información se obtiene a través de las órdenes de compra que sean enlazadas a los distintos
                    ítems que componen los proyectos del plan anual de compras.
                  </Strong>
                </Typography>
              </Column>
            </Row>
          )}
        </>
      )}
      {error && (
        <Card
          display="flex"
          enableBorder
          color="blank"
          margin="43px 0 44px 0"
          alignItems="center"
          height={isTiny ? '139px' : '243px'}
        >
          <CardItem direction="row" padding="25px 16px">
            <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
            <Typography
              color="black1"
              variant="body1"
              fontWeight="regular"
              fontSize="14px"
              lineHeight="18px"
              margin="0 0 0 33px"
              tabIndex="0"
              sx={{ maxWidth: '520px', 'white-space': 'pre-wrap' }}
            >
              {ANNUAL_PURCHASE_PLAN_TABS.SERVICE_ERROR}
            </Typography>
          </CardItem>
        </Card>
      )}
      {isContentReady && !hasData && (
        <Card display="flex" color="blank" enableBorder margin="42px 0 32px 0">
          <CardItem direction="column">
            <NotFoundImg
              alt="No se encontraron resultados"
              margin="52px 0 0 0"
              src={`${PUBLIC_URL}/assets/images/notFound.svg`}
            />
            <Typography
              color="black1"
              variant="h4"
              fontWeight="bold"
              fontSize="18px"
              lineHeight="22px"
              margin="10px 0 0 0"
              tabIndex="0"
            >
              {ANNUAL_PURCHASE_PLAN_TABS.NO_INFO}
            </Typography>
            <Typography
              color="black3"
              variant="body2"
              fontWeight="regular"
              fontSize="14px"
              lineHeight="18px"
              margin="5px 0 52px 0"
              tabIndex="0"
              sx={{ maxWidth: '270px', textAlign: 'center' }}
            >
              {ANNUAL_PURCHASE_PLAN_TABS.ANNUAL_PURCHASE_PLAN_EXECUTION.NO_DATA}
            </Typography>
          </CardItem>
        </Card>
      )}
      {hasDataToShow && (
        <>
          <Row sx={{ justifyContent: 'flex-end' }} margin="0 0 10px 0">
            <IconButton
              singleIcon
              disabled={!showTable}
              margin="24px 10px 0 0"
              type="toggleChart"
              variant="outlined"
              onClick={handleShowTable(false)}
            />
            <IconButton
              singleIcon
              disabled={showTable}
              margin="24px 0 0"
              type="toggleTable"
              variant="outlined"
              onClick={handleShowTable(true)}
            />
          </Row>
          {!showTable ? (
            <Card color="blank" enableBorder padding="15px 16px 24px 13px">
              <Row>
                <InfoLabel label="Millones de pesos (CLP)" margin="0 0 16px" fontSize="10px" />
              </Row>
              <ColumnChart categories={categories} data={chartData} tickScale={tickScale} />
              <Row sx={{ justifyContent: 'space-between' }}>
                <Column padding="0">
                  <InfoLabel
                    color={chartData[0].color}
                    label={chartData[0].footer}
                    margin="0 24px 0 0"
                    fontSize="10px"
                  />
                  <InfoLabel color={chartData[1].color} label={chartData[1].footer} fontSize="10px" />
                </Column>
                <Typography
                  color="black3"
                  display="inline"
                  variant="body2"
                  fontWeight="regular"
                  fontSize="14px"
                  lineHeight="18px"
                  sx={{ margin: { xs: '10px 0 0 0', sm: '0' } }}
                >
                  Última actualización: {updateDate}
                </Typography>
              </Row>
            </Card>
          ) : (
            <Card display="flex" color="blank" enableBorder padding="5px 16px 24px 16px">
              <DataTable
                headers={[
                  { label: 'Mes', value: 'month' },
                  { label: 'Año', value: 'year' },
                  { label: 'Monto planificado para el mes', value: 'plannedAmount' },
                  { label: 'Monto ejecutado en el mes', value: 'executedAmount' },
                  { label: 'Porcentaje ejecutado', value: 'percent' }
                ]}
                headersCellsProps={[
                  { enableSort: true, enableFilter: true, initialSort: false, width: '120px' },
                  {
                    enableSort: true,
                    enableFilter: true,
                    initialDirection: 'ascending',
                    initialSort: true,
                    width: '70px'
                  },
                  { enableSort: true, enableFilter: true, width: '240px', alignment: 'right' },
                  { enableSort: true, enableFilter: true, width: '240px', alignment: 'right' },
                  { enableSort: true, enableFilter: true, width: '190px', alignment: 'right' }
                ]}
                initialRows={totalRows}
                records={gridData}
                recordsCellsProps={[
                  { alignment: 'left', verticalAlign: 'middle' },
                  { alignment: 'left', verticalAlign: 'middle' },
                  { alignment: 'right', verticalAlign: 'middle' },
                  { alignment: 'right', verticalAlign: 'middle' },
                  { alignment: 'right', verticalAlign: 'middle' }
                ]}
                recordsDecorators={[
                  ({ month }) => month,
                  ({ year }) => year,
                  ({ plannedAmount }) => plannedAmount,
                  ({ executedAmount }) => executedAmount,
                  ({ percent }) => percent
                ]}
                loading={loading}
                enablePagination={false}
                accessibility={{ label: 'Tabla de montos del plan anual de compras' }}
              />
              <Link
                tabIndex={0}
                color="primary"
                textDecoration="underline"
                position={hasPagination ? 'absolute' : 'relative'}
                onClick={handleDownload}
                onKeyPress={handleKeyPressDownload}
              >
                <ArrowDownIcon /> Descargar listado en excel
              </Link>
            </Card>
          )}
        </>
      )}
      {loading && !error && (
        <>
          <Row flexDirection="column" gap="5px">
            <Skeleton height="35px" variant="rectangular" width="50%" />
            <Skeleton height="15px" variant="rectangular" width="70%" />
            <Skeleton height="59px" variant="rectangular" width="70%" />
          </Row>
          <Row justifyContent="flex-end" margin="20px 0 10px 0">
            <Skeleton height="40px" variant="rectangular" width="40px" margin="0 10px 0 0 " />
            <Skeleton height="40px" variant="rectangular" width="40px" />
          </Row>
          <Skeleton height="674px" variant="rectangular" width="100%" />
        </>
      )}
      <Separator />
    </Root>
  )
}

export default AnnualPurchasePlanSection
